import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RecommendationCard = ({ recommendation, isSelected, onSelect, onStatusUpdate }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const getCategoryColor = (category) => {
    const colors = {
      'Governance': 'bg-purple-50 text-purple-700 border-purple-200',
      'Analytics': 'bg-blue-50 text-blue-700 border-blue-200',
      'Infrastructure': 'bg-teal-50 text-teal-700 border-teal-200',
      'Strategy': 'bg-[#E6F0F5] text-[#002B45] border-[#002B45]/20',
      'Skills': 'bg-orange-50 text-orange-700 border-orange-200'
    };
    return colors?.[category] || 'bg-gray-50 text-gray-700 border-gray-200';
  };

  const getPriorityBadge = (priority) => {
    if (priority === 1) return { label: 'High Priority', color: 'bg-red-100 text-red-800', icon: 'AlertCircle' };
    if (priority === 2) return { label: 'Medium Priority', color: 'bg-yellow-100 text-yellow-800', icon: 'AlertTriangle' };
    return { label: 'Low Priority', color: 'bg-green-100 text-green-800', icon: 'Info' };
  };

  const handleStatusUpdate = async (newStatus) => {
    setIsUpdating(true);
    await onStatusUpdate(recommendation?.id, newStatus);
    setIsUpdating(false);
  };

  const priorityBadge = getPriorityBadge(recommendation?.priority);
  const isCompleted = recommendation?.status === 'completed';

  return (
    <div
      className={`bg-white rounded-xl shadow-elevation-1 overflow-hidden transition-all duration-300 ${
        isSelected ? 'ring-2 ring-[#1FB5A5] shadow-elevation-2' : 'hover:shadow-elevation-2'
      } ${isCompleted ? 'opacity-75' : ''}`}
    >
      {/* Card Header */}
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-3">
              <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getCategoryColor(recommendation?.category)}`}>
                {recommendation?.category}
              </span>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${priorityBadge?.color} flex items-center gap-1`}>
                <Icon name={priorityBadge?.icon} size={12} />
                {priorityBadge?.label}
              </span>
              {isCompleted && (
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 flex items-center gap-1">
                  <Icon name="CheckCircle" size={12} />
                  Completed
                </span>
              )}
            </div>
            <h3 className="text-lg font-bold text-[#002B45] mb-2">{recommendation?.title}</h3>
            <p className="text-sm text-gray-600">{recommendation?.description}</p>
          </div>
          {!isCompleted && (
            <button
              onClick={() => onSelect(recommendation?.id)}
              className={`ml-4 w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-300 ${
                isSelected
                  ? 'bg-[#1FB5A5] text-white'
                  : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
              }`}
            >
              <Icon name={isSelected ? 'CheckSquare' : 'Square'} size={20} />
            </button>
          )}
        </div>

        {/* Estimated Impact */}
        {recommendation?.estimatedImpact && (
          <div className="bg-gradient-to-r from-green-50 to-teal-50 border border-green-200 rounded-lg p-4 mb-4">
            <p className="text-xs font-semibold text-green-900 mb-2 flex items-center gap-1">
              <Icon name="TrendingUp" size={14} />
              Estimated Impact
            </p>
            <p className="text-sm text-green-700 font-medium">{recommendation?.estimatedImpact}</p>
          </div>
        )}

        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <Icon name="Clock" size={16} className="text-gray-500 mx-auto mb-1" />
            <p className="text-xs text-gray-600">Timeline</p>
            <p className="text-sm font-semibold text-[#002B45]">3-6 months</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <Icon name="Users" size={16} className="text-gray-500 mx-auto mb-1" />
            <p className="text-xs text-gray-600">Resources</p>
            <p className="text-sm font-semibold text-[#002B45]">2-3 people</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <Icon name="DollarSign" size={16} className="text-gray-500 mx-auto mb-1" />
            <p className="text-xs text-gray-600">Investment</p>
            <p className="text-sm font-semibold text-[#002B45]">Medium</p>
          </div>
        </div>

        {/* Expandable Details */}
        {isExpanded && (
          <div className="space-y-4 pt-4 border-t border-gray-200">
            {/* Recommended Actions */}
            {recommendation?.recommendedActions?.length > 0 && (
              <div>
                <p className="text-sm font-semibold text-[#002B45] mb-3 flex items-center gap-2">
                  <Icon name="ListChecks" size={16} className="text-[#1FB5A5]" />
                  Implementation Steps
                </p>
                <ul className="space-y-2">
                  {recommendation?.recommendedActions?.map((action, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-sm text-gray-700">
                      <div className="w-6 h-6 rounded-full bg-[#1FB5A5]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-xs font-semibold text-[#1FB5A5]">{idx + 1}</span>
                      </div>
                      <span>{action?.action}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Resources */}
            {recommendation?.resources?.length > 0 && (
              <div>
                <p className="text-sm font-semibold text-[#002B45] mb-3 flex items-center gap-2">
                  <Icon name="BookOpen" size={16} className="text-[#1FB5A5]" />
                  Supporting Resources
                </p>
                <div className="flex flex-wrap gap-2">
                  {recommendation?.resources?.map((resource, idx) => (
                    <a
                      key={idx}
                      href={resource?.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg text-xs hover:bg-blue-100 transition-colors"
                    >
                      <Icon name="ExternalLink" size={12} />
                      {resource?.title}
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* ROI Projection */}
            <div className="bg-gradient-to-r from-[#1FB5A5]/10 to-[#002B45]/10 rounded-lg p-4">
              <p className="text-sm font-semibold text-[#002B45] mb-3 flex items-center gap-2">
                <Icon name="TrendingUp" size={16} className="text-[#1FB5A5]" />
                ROI Projection
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-600 mb-1">Expected ROI</p>
                  <p className="text-2xl font-bold text-[#1FB5A5]">250%</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 mb-1">Payback Period</p>
                  <p className="text-2xl font-bold text-[#002B45]">8 months</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2 mt-4">
          <Button
            onClick={() => setIsExpanded(!isExpanded)}
            variant="outline"
            size="sm"
            iconName={isExpanded ? 'ChevronUp' : 'ChevronDown'}
            iconPosition="right"
            className="flex-1"
          >
            {isExpanded ? 'Show Less' : 'View Details'}
          </Button>
          {!isCompleted && (
            <Button
              onClick={() => handleStatusUpdate('completed')}
              disabled={isUpdating}
              size="sm"
              iconName="CheckCheck"
              iconPosition="left"
            >
              {isUpdating ? 'Updating...' : 'Mark Complete'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecommendationCard;